import { useEffect, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';

import { nudosGray850 } from '../../../../styles/styles.constants';
import BordOneToneIcon from '../../../NudosComponents/BordOneToneIcon/BordOneToneIcon';
import NudosButton from '../../../NudosComponents/NudosButton/NudosButton';

import './CommentImage.scss';

interface ICommentImage {
  imageUrl?: string;
  displayDownloadButton?: boolean;
  useCustomerChatExpandedImageModal?: boolean;
}

const CommentImage = ({
  imageUrl,
  displayDownloadButton = true,
  useCustomerChatExpandedImageModal = false
}: ICommentImage) => {
  const [showImageModal, setShowImageModal] = useState(false);

  const handleDownloadImage = () => {
    if (!imageUrl) return;
    window.open(imageUrl, '_blank');
  };

  const handleOverlayClick = () => {
    setShowImageModal(false);
  };

  const handleModalContentClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const handleInlineDownloadClick = (event: MouseEvent) => {
    event.stopPropagation();
    handleDownloadImage();
  };

  useEffect(() => {
    if (!showImageModal || !useCustomerChatExpandedImageModal) return;

    const handleEscapeKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setShowImageModal(false);
    };

    window.addEventListener('keydown', handleEscapeKeyDown, true);
    return () => window.removeEventListener('keydown', handleEscapeKeyDown, true);
  }, [showImageModal, useCustomerChatExpandedImageModal]);

  if (!imageUrl) return null;

  const imageModalOverlayClassName = [
    'imageModalOverlay',
    useCustomerChatExpandedImageModal && 'imageModalOverlayCustomerChat',
    useCustomerChatExpandedImageModal && 'imageModalOverlayPortaled'
  ]
    .filter(Boolean)
    .join(' ');

  const imageModalOverlay = showImageModal ? (
    <div className={imageModalOverlayClassName} onClick={handleOverlayClick}>
      {useCustomerChatExpandedImageModal ? (
        <div className="imageModalContent imageModalContentCustomerChat" onClick={handleModalContentClick}>
          <div className="imageModalStage">
            <img src={imageUrl} alt="Vista ampliada de la imagen" className="imageModalCustomerChatImage" />
            <div className="imageModalCustomerChatActions">
              <button type="button" className="imageModalInlineDownload" onClick={handleInlineDownloadClick}>
                <span>Descargar</span>
                <BordOneToneIcon variant="download" stroke="#ffffff" standardSize={16} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="imageModalContent" onClick={handleModalContentClick}>
          <img src={imageUrl} alt="Vista ampliada de la imagen" className="fullsizeImage" />

          <div className="modalActionButtons">
            <NudosButton
              buttonText="Cerrar"
              handleClick={() => setShowImageModal(false)}
              customWidth="180px"
              customHeight="40px"
              colorPalette="secondary"
            />
            <NudosButton
              buttonText="Descargar imagen"
              handleClick={handleDownloadImage}
              customWidth="180px"
              customHeight="40px"
              customClassName="darkButton"
            />
          </div>
        </div>
      )}
    </div>
  ) : null;

  const renderedImageModalOverlay =
    imageModalOverlay &&
    (useCustomerChatExpandedImageModal && typeof document !== 'undefined'
      ? createPortal(imageModalOverlay, document.body)
      : imageModalOverlay);

  return (
    <div className="commentImage">
      <div className="imagePreviewContainer">
        <div className="imagePreviewWrapper" onClick={() => setShowImageModal(true)}>
          <img src={imageUrl} alt="Imagen adjunta al comentario" className="previewImage" />
        </div>

        {displayDownloadButton && (
          <div className="downloadButton" onClick={handleDownloadImage}>
            <div>Descargar</div>
            <BordOneToneIcon variant="download" className="downloadButtonIcon" stroke={nudosGray850} />
          </div>
        )}
      </div>

      {renderedImageModalOverlay}
    </div>
  );
};

export default CommentImage;
